// The pricing cards: three plan cards with payment options revealed
// inside the selected one, plus the "what all plans include" panel that
// stands in Base's slot when the flags hide its card. A reusable grid —
// the quote screen renders it today, and the end-of-application selection
// screen will render the same component with post-answer pricing. The
// flag filtering (`visiblePlans` / `hiddenBaselineFeatures`) lives here
// beside it so every consumer honors the PostHog flags the same way.

import {
  CheckIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import type { QuotePaymentOption, QuotePlan } from "./api";
import type { QuoteFlags } from "./flags";
import {
  compareOptions,
  dollars,
  monthlyTermsLine,
  optionLabel,
} from "./payment-copy";

/** Display copy the pricing engine doesn't carry — pure presentation,
 * keyed by plan slug. The skeleton renders the same identity (name
 * included) before the server has said a word. */
export const PLAN_COPY: Record<
  string,
  {
    name: string;
    kicker: string;
    kickerClass: string;
    tagline: string;
    pills: { label: string; missing?: boolean; icon: "lock" | "plane" }[];
    /** What the plan is called when it is the ONLY plan: the price
     * card's headline, its supporting line, and the selection bar's
     * title. Authored per plan rather than derived from the pills,
     * because a plan whose guarantees are absences (Base) needs copy
     * about what it IS, not a negated headline. */
    solo: { headline: string; support: string; barTitle: string };
    /** Where the card sits while the cards stack (below `lg`): the most
     * popular first, then up, then the essentials. */
    stackedOrder: string;
  }
> = {
  simple: {
    name: "Base",
    kicker: "Essentials",
    kickerClass: "text-evergreen",
    tagline: "The essentials, done with care.",
    pills: [
      { label: "No price lock", missing: true, icon: "lock" },
      { label: "No travel protection", missing: true, icon: "plane" },
    ],
    solo: {
      // Base's guarantees are absences, so its headline is about the
      // care it does cover rather than a lock it does not have.
      headline: "Everything you need, covered",
      support: "The essentials, done with care.",
      barTitle: "Your plan with After.com",
    },
    stackedOrder: "order-3 lg:order-1",
  },
  value: {
    name: "Crest",
    kicker: "Most popular",
    kickerClass: "text-cantelope-600",
    tagline: "Price-lock guarantee and nationwide travel protection.",
    pills: [
      { label: "Price locked for life", icon: "lock" },
      { label: "Nationwide travel protection", icon: "plane" },
    ],
    solo: {
      headline: "Price-locked for life",
      support: "Including nationwide travel protection",
      barTitle: "Price-lock with After.com",
    },
    stackedOrder: "order-1 lg:order-2",
  },
  choice: {
    name: "Summit",
    kicker: "Comprehensive",
    kickerClass: "text-evergreen",
    tagline: "All the bells and whistles for ultimate peace of mind.",
    pills: [
      { label: "Price locked for life", icon: "lock" },
      { label: "Worldwide travel protection", icon: "plane" },
    ],
    solo: {
      headline: "Price-locked for life",
      support: "Including worldwide travel protection",
      barTitle: "Price-lock with After.com",
    },
    stackedOrder: "order-2 lg:order-3",
  },
};

export const PLAN_ORDER = ["simple", "value", "choice"];

/** The single-plan copy for a plan the client has never heard of — a
 * package added server-side needs no deploy to render honestly. Claims
 * nothing about guarantees. */
const SOLO_FALLBACK = {
  headline: "Everything you need, covered",
  support: "The care your plan includes.",
  barTitle: "Your plan with After.com",
};

/** The price card's headline, its supporting line, and the selection
 * bar's title when this plan is the only one on offer. */
export function soloCopy(slug: string) {
  return PLAN_COPY[slug]?.solo ?? SOLO_FALLBACK;
}

/** The DOM id a plan's card renders under — the quote screen scrolls the
 * quiz's match into view by it. */
export function planCardId(slug: string): string {
  return `plan-card-${slug}`;
}

function sortedOptions(plan: QuotePlan): QuotePaymentOption[] {
  return [...plan.payment_options].sort(compareOptions);
}

/**
 * What choosing a package selects to pay with: the first way to pay
 * monthly as displayed, else the first option shown. The display order
 * puts single pay first, so "first multi-pay" is the shortest term on
 * offer — whatever the flags let through. The screens that own the
 * selection apply this to every way of choosing a package (the card's
 * button, the quiz's recommendation), so it lives here beside the order
 * it depends on rather than in any one click handler.
 */
export function defaultPaymentOption(
  plan: QuotePlan,
): QuotePaymentOption | null {
  const options = sortedOptions(plan);
  return (
    options.find((option) => option.payment_period !== "single_pay") ??
    options[0] ??
    null
  );
}

/** The mockup's headline price: the cheapest monthly option. */
function startingMonthly(plan: QuotePlan): number | null {
  const monthly = plan.payment_options
    .map((o) => o.recurring_cents)
    .filter((c): c is number => c !== null);
  return monthly.length ? Math.min(...monthly) : null;
}

function paidInFullToday(plan: QuotePlan): number | null {
  const single = plan.payment_options.find(
    (o) => o.payment_period === "single_pay",
  );
  return single ? single.due_today_cents : null;
}

/** What the card's big figure reads: the cheapest monthly, else the one
 * payment, else the placeholder for a plan with nothing offered. */
function headlineAmount(monthly: number | null, inFull: number | null): string {
  if (monthly !== null) return dollars(monthly);
  if (inFull !== null) return dollars(inFull);
  return "—";
}

/**
 * The plans the committed flags let through, with each plan's options
 * narrowed to its listed slugs. Every misconfiguration fails open: an
 * empty plan set, a plan the payload doesn't mention, or a filter that
 * would empty a plan's options all render everything the server offered.
 */
export function visiblePlans(
  plans: QuotePlan[],
  flags: QuoteFlags,
): QuotePlan[] {
  const kept = plans
    .filter((plan) => flags.plan_slugs.includes(plan.slug))
    .map((plan) => {
      const allowed = flags.option_slugs_by_plan?.[plan.slug];
      if (!allowed) return plan;
      const options = plan.payment_options.filter((option) =>
        allowed.includes(option.slug),
      );
      return options.length > 0 ? { ...plan, payment_options: options } : plan;
    });
  return kept.length > 0 ? kept : plans;
}

/**
 * When the flags hide the Base package that the server DID price, its
 * features become the "what all plans include" panel standing in the
 * card's place — the essentials aren't gone, they're the floor. Keyed
 * off what actually renders, so a fail-open that re-admits Base never
 * shows both the card and the panel.
 */
export function hiddenBaselineFeatures(
  served: QuotePlan[],
  shown: QuotePlan[],
): QuotePlan["features"] | null {
  if (shown.some((plan) => plan.slug === "simple")) return null;
  return served.find((plan) => plan.slug === "simple")?.features ?? null;
}

/** Pill tones, on white and on the selected evergreen card. Daisy marks a
 * guarantee the plan HAS; a crossed-out pill on evergreen wears light
 * peach instead — daisy would read as a feature. */
function pillTone(selected: boolean, missing: boolean): string {
  if (selected && missing) {
    return "border-cantelope-200 bg-cantelope-200 text-evergreen";
  }
  if (selected) return "border-daisy bg-daisy text-evergreen";
  if (missing) return "border-oat-200 text-oat-800";
  return "border-evergreen/35 text-soil";
}

export function GuaranteePill({
  pill,
  selected,
}: {
  pill: { label: string; missing?: boolean; icon: "lock" | "plane" };
  selected: boolean;
}) {
  const Icon = pill.missing
    ? XMarkIcon
    : pill.icon === "lock"
      ? LockClosedIcon
      : PaperAirplaneIcon;
  const tone = pillTone(selected, pill.missing ?? false);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] font-bold ${tone}`}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
      {pill.label}
    </span>
  );
}

function PaymentTile({
  option,
  selected,
  cardSelected,
  onSelect,
}: {
  option: QuotePaymentOption;
  selected: boolean;
  cardSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-[10px] border-2 px-3.5 py-3 text-left transition-all ${
        selected
          ? "border-daisy bg-ivory-light/15"
          : "border-transparent bg-ivory-light/5 hover:bg-ivory-light/10"
      }`}
    >
      <span className="flex items-center gap-2.5">
        <span
          className={`flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-full border-2 ${
            selected ? "border-daisy" : "border-ivory-light/55"
          }`}
          aria-hidden="true"
        >
          {selected ? <span className="h-2 w-2 rounded-full bg-daisy" /> : null}
        </span>
        <span
          className={`text-base font-bold ${cardSelected ? "text-ivory" : "text-soil"}`}
        >
          {optionLabel(option)}
        </span>
      </span>
      <span className="mt-1 block pl-[27px]">
        <span
          className={`text-xl font-bold tracking-tight ${
            selected ? "text-daisy" : "text-ivory"
          }`}
        >
          {dollars(
            option.recurring_cents !== null
              ? option.recurring_cents
              : option.due_today_cents,
          )}
        </span>
        <span className="ml-1 text-[13px] text-ivory/80">
          {option.recurring_cents !== null ? "/mo" : "one-time"}
        </span>
      </span>
      {option.recurring_cents !== null ? (
        <span className="block pl-[27px] text-[13px] leading-snug text-ivory/80">
          {monthlyTermsLine(option)}
        </span>
      ) : null}
    </button>
  );
}

/**
 * The price element when a plan has exactly one way to pay: the payment
 * tile's content promoted into the card — the option's name, its price,
 * its terms. Choosing the package chooses it (the grid auto-selects), so
 * nothing else needs asking.
 */
function SoleOptionPricing({
  option,
  selected,
  muted,
}: {
  option: QuotePaymentOption;
  selected: boolean;
  muted: string;
}) {
  const isMonthly = option.recurring_cents !== null;
  return (
    <>
      <span
        className={`text-[13px] font-bold uppercase tracking-[0.14em] ${muted}`}
      >
        {optionLabel(option)}
      </span>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span
          className={`text-[31px] font-bold leading-none tracking-tight lg:text-[39px] ${
            selected ? "text-daisy" : "text-soil"
          }`}
        >
          {dollars(
            isMonthly && option.recurring_cents !== null
              ? option.recurring_cents
              : option.due_today_cents,
          )}
        </span>
        {isMonthly ? <span className={`text-sm ${muted}`}>/mo</span> : null}
      </div>
      <p className={`mt-1 text-sm leading-snug ${muted}`}>
        {isMonthly ? monthlyTermsLine(option) : "one-time"}
      </p>
    </>
  );
}

function PlanCard({
  plan,
  selected,
  chosenOptionKey,
  onSelectPlan,
  onSelectOption,
}: {
  plan: QuotePlan;
  selected: boolean;
  chosenOptionKey: string | null;
  onSelectPlan: () => void;
  onSelectOption: (option: QuotePaymentOption) => void;
}) {
  const copy = PLAN_COPY[plan.slug];
  const monthly = startingMonthly(plan);
  const inFull = paidInFullToday(plan);
  // Exactly one way to pay: the price element becomes that option — its
  // name where "Starting at" was, its price, its terms — and the radio
  // section never renders. "Starting at" over the only price would invite
  // the question of what it starts toward.
  const soleOption =
    plan.payment_options.length === 1 ? plan.payment_options[0] : null;
  const muted = selected ? "text-ivory/90" : "text-oat-700";
  const strong = selected ? "text-ivory" : "text-soil";

  return (
    <div
      id={planCardId(plan.slug)}
      className={`flex flex-col gap-4 rounded-2xl border-2 p-4 transition-all 2xs:p-6 ${
        copy?.stackedOrder ?? ""
      } ${
        selected
          ? "border-evergreen bg-evergreen text-ivory shadow-xl"
          : "border-oat-200 bg-white text-soil shadow-sm"
      }`}
    >
      <span
        className={`text-[13px] font-bold uppercase tracking-[0.14em] ${
          selected ? "text-daisy" : (copy?.kickerClass ?? "text-evergreen")
        }`}
      >
        {copy?.kicker ?? plan.name}
      </span>

      <div>
        <h2
          className={`font-serif text-[25px] leading-tight lg:text-[31px] ${strong}`}
        >
          {plan.name} <span className={muted}>package</span>
        </h2>
        {copy ? (
          <p className={`mt-1 min-h-[48px] text-base leading-normal ${muted}`}>
            {copy.tagline}
          </p>
        ) : null}
      </div>

      <div
        className={`rounded-xl px-3 py-4 2xs:px-4 ${
          selected ? "bg-ivory-light/10" : "bg-oat-50"
        }`}
      >
        {soleOption ? (
          <SoleOptionPricing
            option={soleOption}
            selected={selected}
            muted={muted}
          />
        ) : (
          <>
            <span
              className={`text-[13px] font-bold uppercase tracking-[0.14em] ${muted}`}
            >
              Starting at
            </span>
            <div className="mt-1 flex items-baseline gap-1.5">
              <span
                className={`text-[31px] font-bold leading-none tracking-tight lg:text-[39px] ${
                  selected ? "text-daisy" : "text-soil"
                }`}
              >
                {headlineAmount(monthly, inFull)}
              </span>
              <span className={`text-sm ${muted}`}>per month</span>
            </div>
            {inFull !== null ? (
              <p
                className={`mt-2 border-t pt-2 text-base ${muted} ${
                  selected ? "border-ivory-light/20" : "border-oat-200"
                }`}
              >
                or <b className={strong}>{dollars(inFull)}</b> paid in full
                today
              </p>
            ) : null}
          </>
        )}
        <button
          type="button"
          onClick={onSelectPlan}
          className={`mt-4 flex w-full items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-base font-bold transition-all ${
            selected
              ? "border-transparent bg-transparent text-daisy"
              : "border-soil bg-soil text-ivory-light hover:bg-soil/90"
          }`}
        >
          {selected ? (
            <>
              <CheckIcon
                className="h-4 w-4"
                strokeWidth={3}
                aria-hidden="true"
              />
              Selected
            </>
          ) : (
            `Choose ${plan.name}`
          )}
        </button>
      </div>

      {selected && !soleOption ? (
        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-ivory/80">
            How you&rsquo;d like to pay
          </span>
          {sortedOptions(plan).map((option) => (
            <PaymentTile
              key={option.slug}
              option={option}
              selected={chosenOptionKey === option.slug}
              cardSelected={selected}
              onSelect={() => onSelectOption(option)}
            />
          ))}
        </div>
      ) : null}

      {copy ? (
        <div className="flex flex-col items-start gap-2">
          {copy.pills.map((pill) => (
            <GuaranteePill key={pill.label} pill={pill} selected={selected} />
          ))}
        </div>
      ) : null}

      <ul className="flex flex-col gap-2.5">
        {plan.features.map((feature) => (
          <li key={feature.label} className="flex items-start gap-2.5">
            <CheckIcon
              className={`mt-0.5 h-4 w-4 shrink-0 ${
                selected ? "text-daisy" : "text-evergreen"
              }`}
              strokeWidth={3}
              aria-hidden="true"
            />
            <span className={`text-base leading-snug ${strong}`}>
              {feature.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The coastline scene the marketing pricing page paints on its Base
 * card (after-www /pricing) — here it settles into the panel's bottom
 * whitespace. Decorative only. */
export function IncludedInAllIllustration() {
  return (
    <img
      src="/public/base-texture.webp"
      alt=""
      aria-hidden="true"
      // Bleeds through the panel's right padding to sit flush with its
      // edge (the panel clips at the rounded corner).
      className="pointer-events-none -mr-6 mt-auto w-[calc(100%+1.5rem)] max-w-none select-none object-contain opacity-70"
    />
  );
}

/**
 * Stands where the Base card would: the essentials every package builds
 * on, listed from the server's own Base features. Deliberately not
 * dressed as a choosable plan — no price, no button, softer ground.
 */
export function IncludedInAllPanel({
  features,
}: {
  features: QuotePlan["features"];
}) {
  return (
    <div
      className={`flex flex-col gap-4 overflow-hidden rounded-2xl border border-oat-200 bg-oat-50 p-4 text-soil 2xs:p-6 ${
        PLAN_COPY.simple?.stackedOrder ?? ""
      }`}
    >
      <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-evergreen">
        The essentials
      </span>
      <div>
        <h2 className="font-serif text-[25px] leading-tight text-soil lg:text-[31px]">
          What all plans <span className="text-oat-700">include</span>
        </h2>
        <p className="mt-1 min-h-[48px] text-base leading-normal text-oat-700">
          Every package builds on the same essential care.
        </p>
      </div>
      <ul className="flex flex-col gap-2.5">
        {features.map((feature) => (
          <li key={feature.label} className="flex items-start gap-2.5">
            <CheckIcon
              className="mt-0.5 h-4 w-4 shrink-0 text-evergreen"
              strokeWidth={3}
              aria-hidden="true"
            />
            <span className="text-base leading-snug text-soil">
              {feature.label}
            </span>
          </li>
        ))}
      </ul>
      <IncludedInAllIllustration />
    </div>
  );
}

/**
 * The two guarantees as filled badges. Daisy means "a guarantee this
 * plan HAS" on the evergreen card (see `pillTone`), but on the light
 * ground of the covered card it reads as a highlighter and pulls the
 * eye off the price, which is the one thing daisy should mark here.
 * Soil on oat instead.
 */
function GuaranteeBadges({ slug }: { slug: string }) {
  // Only guarantees the plan HAS. A filled badge reads as a feature, so
  // a `missing` pill rendered here would turn "No price lock" into a
  // selling point — the crossed-out treatment that carries the negation
  // only exists on the comparison card.
  const pills = (PLAN_COPY[slug]?.pills ?? []).filter((pill) => !pill.missing);
  if (pills.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {pills.map((pill) => {
        const Icon = pill.icon === "lock" ? LockClosedIcon : PaperAirplaneIcon;
        return (
          <span
            key={pill.label}
            className="inline-flex items-center gap-1.5 rounded-full border border-soil bg-soil px-3 py-1.5 text-[13px] font-bold text-oat"
          >
            <Icon
              className="h-3.5 w-3.5"
              strokeWidth={2.5}
              aria-hidden="true"
            />
            {pill.label}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Everything the plan covers, as ONE list: the baseline every package
 * would have shared, then the plan's own features. No seam between
 * them, because a customer with one package has no second one to sort
 * them against — "what all plans include" needs other plans to be true.
 */
export function CoveredCard({
  plan,
  baselineFeatures,
}: {
  plan: QuotePlan;
  baselineFeatures: QuotePlan["features"] | null;
}) {
  const features = [...(baselineFeatures ?? []), ...plan.features];
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-oat-200 bg-oat-50 p-4 text-soil 2xs:p-6">
      <div>
        <h2 className="font-serif text-[25px] leading-tight text-soil lg:text-[31px]">
          Your future is covered
        </h2>
        <p className="mt-1 text-base leading-normal text-oat-700">
          Your plan includes everything you need for ultimate peace of mind
        </p>
      </div>
      <GuaranteeBadges slug={plan.slug} />
      <ul className="flex flex-col gap-2.5">
        {features.map((feature) => (
          <li key={feature.label} className="flex items-start gap-2.5">
            <CheckIcon
              className="mt-0.5 h-4 w-4 shrink-0 text-evergreen"
              strokeWidth={3}
              aria-hidden="true"
            />
            <span className="text-base leading-snug text-soil">
              {feature.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The price side of the single-plan screen. Leads with the guarantee
 * rather than the package name, and carries no Choose button: with
 * nothing to choose between, the button selected the only option, and
 * Continue in the selection bar is the real commitment. The guarantee
 * pills and the feature list live on the covered card instead.
 */
export function SinglePlanPriceCard({
  plan,
  chosenOptionKey,
  onSelectOption,
}: {
  plan: QuotePlan;
  chosenOptionKey: string | null;
  onSelectOption: (option: QuotePaymentOption) => void;
}) {
  const monthly = startingMonthly(plan);
  const inFull = paidInFullToday(plan);
  const copy = soloCopy(plan.slug);
  const soleOption =
    plan.payment_options.length === 1 ? plan.payment_options[0] : null;
  return (
    <div className="flex flex-col gap-4 rounded-2xl border-2 border-evergreen bg-evergreen p-4 text-ivory shadow-xl 2xs:p-6">
      <div>
        <h2 className="font-serif text-[25px] leading-tight text-ivory lg:text-[31px]">
          {copy.headline}
        </h2>
        <p className="mt-1 text-base leading-normal text-ivory/90">
          {copy.support}
        </p>
      </div>

      <div className="rounded-xl bg-ivory-light/10 px-3 py-4 2xs:px-4">
        {soleOption ? (
          <SoleOptionPricing
            option={soleOption}
            selected
            muted="text-ivory/90"
          />
        ) : (
          <>
            <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-ivory/90">
              Starting at
            </span>
            <div className="mt-1 flex items-baseline gap-1.5">
              <span className="text-[31px] font-bold leading-none tracking-tight text-daisy lg:text-[39px]">
                {headlineAmount(monthly, inFull)}
              </span>
              <span className="text-sm text-ivory/90">per month</span>
            </div>
            {inFull !== null ? (
              <p className="mt-2 border-t border-ivory-light/20 pt-2 text-base text-ivory/90">
                or <b className="text-ivory">{dollars(inFull)}</b> paid in full
                today
              </p>
            ) : null}
          </>
        )}
      </div>

      {soleOption ? null : (
        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-bold uppercase tracking-[0.14em] text-ivory/80">
            How you&rsquo;d like to pay
          </span>
          {sortedOptions(plan).map((option) => (
            <PaymentTile
              key={option.slug}
              option={option}
              selected={chosenOptionKey === option.slug}
              cardSelected
              onSelect={() => onSelectOption(option)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The single-plan screen's cards. Covered card first in the DOM, which
 * puts it left on desktop and first in the stack on mobile — the plan
 * is what the customer came to read, and the price follows it.
 */
export function SinglePlanCards({
  plan,
  baselineFeatures,
  chosenOptionSlug,
  onSelectOption,
}: {
  plan: QuotePlan;
  baselineFeatures: QuotePlan["features"] | null;
  chosenOptionSlug: string | null;
  onSelectOption: (option: QuotePaymentOption) => void;
}) {
  return (
    <div className="mx-auto flex max-w-[1080px] flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-stretch lg:gap-6">
      <CoveredCard plan={plan} baselineFeatures={baselineFeatures} />
      <SinglePlanPriceCard
        plan={plan}
        chosenOptionKey={chosenOptionSlug}
        onSelectOption={onSelectOption}
      />
    </div>
  );
}

export default function PricingCards({
  plans,
  includedInAllFeatures,
  selectedPlanSlug,
  chosenOptionSlug,
  onSelectPlan,
  onSelectOption,
}: {
  /** The flag-filtered plans (see `visiblePlans`), any order. */
  plans: QuotePlan[];
  /** Base's features when its card is hidden (see
   * `hiddenBaselineFeatures`); null renders no panel. */
  includedInAllFeatures: QuotePlan["features"] | null;
  selectedPlanSlug: string | null;
  chosenOptionSlug: string | null;
  onSelectPlan: (slug: string) => void;
  onSelectOption: (planSlug: string, option: QuotePaymentOption) => void;
}) {
  const ordered = [...plans].sort(
    (a, b) => PLAN_ORDER.indexOf(a.slug) - PLAN_ORDER.indexOf(b.slug),
  );
  // Three across only from `lg`: at tablet widths three columns squeeze
  // the prices and buttons onto extra lines, so the cards stack instead.
  return (
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-3 lg:items-stretch lg:gap-5">
      {includedInAllFeatures ? (
        <IncludedInAllPanel features={includedInAllFeatures} />
      ) : null}
      {ordered.map((plan) => (
        <PlanCard
          key={plan.slug}
          plan={plan}
          selected={plan.slug === selectedPlanSlug}
          chosenOptionKey={chosenOptionSlug}
          onSelectPlan={() => {
            // The selected card's button is a label, not a control —
            // re-choosing the package must not undo a chosen option.
            if (plan.slug === selectedPlanSlug) return;
            onSelectPlan(plan.slug);
          }}
          onSelectOption={(option) => onSelectOption(plan.slug, option)}
        />
      ))}
    </div>
  );
}
